import React from 'react';

import Tippy, { TippyProps } from '@tippyjs/react';

import { Col, Row } from './Flex';
import { Interactive, InteractiveOnClick } from './InteractiveElement';
import { ArrowKeysTrap } from './KeyboardNavigation';

import {
	useA11y,
	useComponentDefaultProps,
	useHandleEscKey,
	useList,
} from 'domain/hooks';
import { createId, noop } from 'domain/utils';
import { DivProps } from './common-types';
import { cn } from 'domain/core';

type OpenEvent = 'left-click' | 'right-click';

type IMenuContext = {
	opened: boolean;
	id: string;
	activeChild?: string;
	items: string[];
	on: OpenEvent;
	closeOnOptionClick: boolean;
	setActiveChild(childId?: string): void;
	setOpen(open: boolean): void;
	registerChild(id: string): void;
	removeChild(id: string): void;
};

const MenuContext = React.createContext<IMenuContext>({
	id: '',
	opened: false,
	on: 'left-click',
	items: [],
	closeOnOptionClick: true,
	setActiveChild: noop,
	setOpen: noop,
	registerChild: noop,
	removeChild: noop,
});

const useMenuContext = () => React.useContext(MenuContext);

export type MenuRef = {
	close: VoidFunction;
	open: VoidFunction;
};

interface MenuProps extends React.PropsWithChildren {
	id?: string;
	className?: string;
	on?: OpenEvent;
	placement?: TippyProps['placement'];
	closeOnOptionClick?: boolean;
}

const defaultProps: Partial<MenuProps> = {
	id: createId('menu_'),
	on: 'left-click',
	placement: 'bottom',
	closeOnOptionClick: true,
};

const Root = React.forwardRef<MenuRef, MenuProps>((props, ref) => {
	const _props = useComponentDefaultProps(defaultProps, props);

	const [open, setOpen] = React.useState(false);
	const [activeChild, setActiveChild] = React.useState<string | undefined>();
	const [items, itemsFns] = useList<string>([]);

	React.useImperativeHandle(
		ref,
		() => ({
			open: () => setOpen(true),
			close: () => setOpen(false),
		}),
		[]
	);

	useHandleEscKey(open, () => setOpen(false));

	const registerChild = React.useCallback(
		(id: string) => {
			if (!itemsFns.includes(id)) {
				itemsFns.append(id);
			}
		},
		[itemsFns]
	);

	const removeChild = React.useCallback(
		(id: string) => {
			itemsFns.filter((i) => i !== id);
		},
		[itemsFns]
	);

	const { children, trigger } = React.useMemo(() => {
		let children: React.ReactNode[] = [],
			trigger: any;

		React.Children.forEach(_props.children, (child, i) => {
			if (React.isValidElement(child)) {
				if (child.type === Trigger || child.props.isTrigger) {
					trigger = child;
				} else {
					children.push(child);
				}
			}
		});

		return {
			children,
			trigger,
		};
	}, [_props.children]);

	React.useEffect(() => {
		if (open) {
			for (let childId of items) {
				const el = document.getElementById(childId);
				if (el && el.ariaDisabled !== 'true') {
					el.focus();
					return;
				}
			}
		}
	}, [open]);

	const toggleOpen = React.useCallback(() => setOpen((o) => !o), [setOpen]);

	const ctx = React.useMemo(
		() => ({
			id: _props.id!,
			on: _props.on!,
			opened: open,
			activeChild,
			closeOnOptionClick: props.closeOnOptionClick!,
			items,
			setOpen,
			setActiveChild,
			registerChild,
			removeChild,
		}),
		[
			_props.id,
			_props.on,
			open,
			activeChild,
			props.closeOnOptionClick,
			items,
			setOpen,
			setActiveChild,
			registerChild,
			removeChild,
		]
	);

	return (
		<MenuContext.Provider value={ctx}>
			<Tippy
				className={cn(
					'rounded border border-neutral-200 bg-neutral-50 p-0 shadow-xl',
					_props.className
				)}
				appendTo={document.body}
				content={children}
				interactive
				visible={open}
				onClickOutside={toggleOpen}
				offset={[0, 4]}
				animation='scale'
				placement={_props.placement}>
				{trigger}
			</Tippy>
		</MenuContext.Provider>
	);
});

interface TriggerProps extends React.PropsWithChildren {
	onOpen?: VoidFunction;
	// Dummy property to locate trigger element. Must set alwais to true
	isTrigger: boolean;
}

const defaultTriggerProps: TriggerProps = {
	onOpen: noop,
	isTrigger: true,
};

const Trigger = React.forwardRef<any, TriggerProps>(
	(props: TriggerProps, ref: any) => {
		const _props = useComponentDefaultProps(defaultTriggerProps, props);

		const { opened, setOpen, on, id } = useMenuContext();

		const handleClick = () => {
			if (on === 'left-click') {
				setOpen(true);
				_props.onOpen!();
			}
			if (opened) {
				setOpen(false);
			}
		};

		const handleOnContextMenu = (evt: any) => {
			if (on === 'right-click') {
				setOpen(true);
				_props.onOpen!();
				evt.preventDefault();
				evt.stopPropagation();
				return false;
			}
		};

		const child = React.Children.only(props.children)
			? React.Children.toArray(props.children)[0]
			: null;

		return child ? (
			<Interactive onInteract={props.onOpen}>
				{React.cloneElement<any>(child as any, {
					ref: ref,
					id: `${id}_trigger`,
					'aria-controls': `${id}_menu`,
					'aria-haspopup': true,
					'aria-expanded': opened,
					onClick: handleClick,
					onContextMenu: handleOnContextMenu,
				})}
			</Interactive>
		) : null;
	}
);
Trigger.displayName = 'MenuTrigger';

interface ItemGroupProps extends React.PropsWithChildren {
	className?: string;
}
const ItemsGroup = (props: ItemGroupProps) => {
	const { id, activeChild } = useMenuContext();

	return (
		<Col
			aria-labelledby={`${id}_trigger`}
			id={`${id}_menu`}
			className={cn(
				'!items-start !justify-start gap-1 bg-neutral-100 p-1',
				props.className
			)}
			role='menu'
			aria-activedescendant={activeChild}>
			<ArrowKeysTrap orientation='vertical'>{props.children}</ArrowKeysTrap>
		</Col>
	);
};

interface ItemProps extends DivProps {
	disabled?: boolean;
	onClick?: InteractiveOnClick;
}

const Item = (props: ItemProps) => {
	const {
		activeChild,
		closeOnOptionClick,
		setOpen,
		setActiveChild,
		registerChild,
		removeChild,
	} = useMenuContext();

	const [title, ariaLabel] = useA11y({
		...props,
		ariaLabelPrefix: 'Menu item',
	});

	const subId = React.useRef(props.id ?? createId('menu_item_')).current;
	const element = React.useRef<HTMLDivElement>();

	const handleFocus = React.useCallback(() => {
		setActiveChild(subId);
	}, [subId, setActiveChild]);

	React.useEffect(() => {
		registerChild(subId);

		return () => removeChild(subId);
	}, []);

	React.useEffect(() => {
		if (activeChild && activeChild === subId) {
			if (document.activeElement?.id !== subId) {
				element.current?.focus();
			}
		}
	}, [activeChild]);

	return (
		<Interactive onInteract={props.onClick} disabled={props.disabled}>
			<Row
				ref={element}
				className={cn(
					'cursor-pointer !justify-start rounded bg-neutral-100 px-4 py-2 hover:bg-primary-200',
					'focus:bg-primary-50',
					'transition-colors',
					props.className
				)}
				role='menuitem'
				id={subId}
				onFocus={handleFocus}
				title={title}
				aria-label={ariaLabel}
				// This needs to be done because the ArrowKeysTrap
				// component passes a new handler for the onKeyPress event,
				// if this new event is not passed the focus trap does not work
				onKeyDown={(props as any).onKeyDown}>
				{props.children}
			</Row>
		</Interactive>
	);
};

interface DividerProps {
	className?: string;
	lineClassName?: string;
}
const Divider = (props: DividerProps) => {
	return (
		<div
			className={cn(
				'side-menu-group-divider',
				'h-auto w-full px-4',
				'box-border',
				props.className
			)}
			tabIndex={-1}
			role='separator'
			aria-orientation='horizontal'>
			<div
				className={cn('h-[2px] w-full bg-neutral-200', props.lineClassName)}
				tabIndex={-1}
			/>
		</div>
	);
};

Root.displayName = 'Menu';

export const Menu = { Root, Trigger, ItemsGroup, Item, Divider };
